import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { IconButton } from "@mui/material";
import { KeyboardArrowLeft, KeyboardArrowRight } from "@mui/icons-material";
import CustomTable from "../../../../../../app/CustomHeadlessTable/CustomTable";
import { unitFromDropdownOrCache } from "../../../../../../app/CustomHeadlessTable/lcaDropdownUnit";
import CustomInputComponent from "../../../../../../app/CustomHeadlessTable/components/CustomInputComponent";
import FileUploadInput from "../../../../../../app/CustomHeadlessTable/components/FileUploadInput";
import {
  addProductionMachines34_2aLCA,
  deleteProductionMachines34_2aLCA,
  getAllProductionMachines34_2aLCA,
  updateProductionMachines34_2aLCA,
} from "../../../../../../apis/Product/CustomManufacturingFlow34Api";
import { getProductProductionMachinesEnergy34_2aDropdown } from "../../../../../../apis/Dropdown";
import { TableCompareTrigger } from "../../../components/lcaComparison";

// Impact categories configuration (16)
const IMPACT_CATEGORIES = [
  { name: "Climate change", unit: "kg CO₂-eq", key: "global_warming" },
  { name: "Ozone depletion", unit: "kg CFC-11-eq", key: "ozone_depletion" },
  { name: "Photochemical ozone formation", unit: "kg NMVOC-eq", key: "smog" },
  { name: "Acidification", unit: "mol H⁺-eq", key: "acidification" },
  {
    name: "Eutrophication, terrestrial",
    unit: "mol N-eq",
    key: "eutrophication_terrestrial",
  },
  {
    name: "Eutrophication, freshwater",
    unit: "kg P-eq",
    key: "eutrophication_freshwater",
  },
  { name: "Eutrophication, marine", unit: "kg N-eq", key: "eutrophication_marine" },
  { name: "Human toxicity, cancer effects", unit: "CTUh", key: "carcinogenics" },
  {
    name: "Human toxicity, non-cancer effects",
    unit: "CTUh",
    key: "non_carcinogenics",
  },
  { name: "Particulate matter", unit: "disease incidence", key: "respiratory_effects" },
  { name: "Ecotoxicity, freshwater", unit: "CTUe", key: "ecotoxicity" },
  { name: "Resource use, energy carriers", unit: "MJ", key: "fossil_fuel_depletion" },
  { name: "Land use", unit: "Pt", key: "land_use" },
  { name: "Water use", unit: "m³ world-eq deprived", key: "water_use" },
  {
    name: "Resource use, mineral and metals",
    unit: "kg Sb-eq",
    key: "resource_use_minerals_metals",
  },
  { name: "Ionising radiation, human health", unit: "kBq U235-eq", key: "ionising_radiation" },
];

const ProductionMachines34_2aTable = ({ productId }) => {
  const { t } = useTranslation();
  const [data, setData] = useState([]);
  const [hiddenColumns, setHiddenColumns] = useState([]);
  const [currentImpactCategoryIndex, setCurrentImpactCategoryIndex] = useState(0);
  const [energyTypeDropdown, setEnergyTypeDropdown] = useState([]);
  const [unitEnergyTypes, setUnitEnergyTypes] = useState({});
  const [fileUploads, setFileUploads] = useState({}); // Store file objects for upload

  // Fetch energy type dropdown (Flow 34.2a)
  useEffect(() => {
    getProductProductionMachinesEnergy34_2aDropdown({
      setDropdown: setEnergyTypeDropdown,
    });
  }, []);

  const toggleColumnVisibility = useCallback((columnId) => {
    setHiddenColumns((prev) =>
      prev.includes(columnId)
        ? prev.filter((id) => id !== columnId)
        : [...prev, columnId]
    );
  }, []);

  const fetchData = useCallback(() => {
    if (!productId) return;
    getAllProductionMachines34_2aLCA({
      productId,
      setObjects: setData,
    });
  }, [productId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Update units when data or dropdown changes
  useEffect(() => {
    if (data.length > 0 && energyTypeDropdown.length > 0) {
      const newUnits = {};
      data.forEach((row) => {
        const energyCode =
          typeof row.energy_type === "string"
            ? row.energy_type
            : row.energy_type?.value || row.energy_type?.code;
        if (energyCode) {
          const selected = energyTypeDropdown.find(
            (opt) =>
              !opt.isCategory &&
              (opt.code === energyCode || opt.value === energyCode)
          );
          if (selected) {
            newUnits[row.id] = selected.unit || "xx";
          }
        }
      });
      setUnitEnergyTypes(newUnits);
    }
  }, [data, energyTypeDropdown]);

  const dynamicPlaceholders = useMemo(
    () => ({
      energy_type: {
        targetField: "qty",
        getPlaceholder: (selectedValue, dropdownData, _rowData, rowId = "new") => {
          const selected = dropdownData?.find((opt) => opt?.value === selectedValue);
          if (selected) {
            setUnitEnergyTypes((prev) => ({
              ...prev,
              [rowId]: selected.unit || "xx",
            }));
          }
        },
        dropdownDataKey: "energy_type",
      },
    }),
    []
  );

  const handleFileUpload = useCallback((file, rowId) => {
    if (file) {
      setFileUploads((prev) => ({
        ...prev,
        [rowId]: file,
      }));
    } else {
      setFileUploads((prev) => {
        const newUploads = { ...prev };
        delete newUploads[rowId];
        return newUploads;
      });
    }
  }, []);

  const handleEdit = useCallback(
    (editedItem) => {
      const energyCode =
        typeof editedItem.energy_type === "string"
          ? editedItem.energy_type
          : editedItem.energy_type?.value || editedItem.energy_type?.code || null;
      const fileToUpload = fileUploads[editedItem.id];
      
      updateProductionMachines34_2aLCA({
        productId,
        rowId: editedItem.id,
        machine_equipment: editedItem.machine_equipment || null,
        energy_type: energyCode,
        qty: editedItem.qty || 0,
        manual_co2e: editedItem.manual_co2e !== null && editedItem.manual_co2e !== undefined ? editedItem.manual_co2e : null,
        documentation: fileToUpload || null,
        objects: data,
        setObjects: setData,
        use_swal: true,
      });
      
      // Clear file upload after submission
      if (fileToUpload) {
        setFileUploads((prev) => {
          const newUploads = { ...prev };
          delete newUploads[editedItem.id];
          return newUploads;
        });
      }
    },
    [productId, data, fileUploads]
  );

  const handleAdd = useCallback(
    (newItem) => {
      const energyCode =
        typeof newItem.energy_type === "string"
          ? newItem.energy_type
          : newItem.energy_type?.value || newItem.energy_type?.code || null;
      const fileToUpload = fileUploads["new"];
      
      addProductionMachines34_2aLCA({
        productId,
        machine_equipment: newItem.machine_equipment || null,
        energy_type: energyCode,
        qty: newItem.qty || 0,
        manual_co2e: newItem.manual_co2e !== null && newItem.manual_co2e !== undefined ? newItem.manual_co2e : null,
        documentation: fileToUpload || null,
        callback: fetchData,
        use_swal: true,
      });
      
      // Clear file upload after submission
      if (fileToUpload) {
        setFileUploads((prev) => {
          const newUploads = { ...prev };
          delete newUploads["new"];
          return newUploads;
        });
      }
    },
    [productId, fetchData, fileUploads]
  );

  const handleDelete = useCallback(
    (itemToDelete) => {
      deleteProductionMachines34_2aLCA({
        productId,
        rowId: itemToDelete.id,
        callback: fetchData,
      });
    },
    [productId, fetchData]
  );

  const currentImpactCategory = IMPACT_CATEGORIES[currentImpactCategoryIndex];
  const impactCategoryAccessor = currentImpactCategory.key;

  const handlePreviousImpactCategory = useCallback(() => {
    setCurrentImpactCategoryIndex((prev) =>
      prev === 0 ? IMPACT_CATEGORIES.length - 1 : prev - 1
    );
  }, []);

  const handleNextImpactCategory = useCallback(() => {
    setCurrentImpactCategoryIndex((prev) =>
      prev === IMPACT_CATEGORIES.length - 1 ? 0 : prev + 1
    );
  }, []);

  const columns = useMemo(
    () => [
      {
        id: "machine_equipment",
        accessor: "machine_equipment",
        header: t("MACHINE_EQUIPMENT") || "Machine/equipment",
        type: "text",
        placeholder: t("USER_TYPES") || "User types...",
        optional: true,
      },
      {
        id: "energy_type",
        accessor: "energy_type",
        header: `${t("ENERGY_TYPE") || "Energy Type"} *`,
        type: "dropdown",
        isCategory: true,
        placeholder: t("SELECT_ENERGY_TYPE") || "Select energy type...",
        unit: (rowId, rowData) =>
          unitFromDropdownOrCache(
            rowData,
            "energy_type",
            energyTypeDropdown,
            unitEnergyTypes,
            rowId
          ),
      },
      {
        id: "qty",
        accessor: "qty",
        header: `${t("QTY_FUNC_UNIT") || "Quantity per functional unit"} *`,
        type: "number",
        placeholder: t("USER_TYPES") || "User types...",
        unit: (rowId, rowData) =>
          unitFromDropdownOrCache(
            rowData,
            "energy_type",
            energyTypeDropdown,
            unitEnergyTypes,
            rowId
          ),
      },
      {
        id: "manual_co2e",
        accessor: "manual_co2e",
        header: `${t("MANUAL_KGCO2E") || "Manual: kgCO₂e per energy unit"}`,
        type: "number",
        placeholder: t("USER_TYPES") || "User types...",
        optional: true,
        unit: (rowId, rowData) =>
          `kgCO₂e/${unitFromDropdownOrCache(
            rowData,
            "energy_type",
            energyTypeDropdown,
            unitEnergyTypes,
            rowId
          )}`,
      },
      {
        id: "documentation",
        accessor: "documentation",
        header: t("DOCUMENTATION") || "Documentation",
        type: "custom",
        placeholder: t("UPLOAD") || "Upload...",
        optional: true,
        cell: ({ row }) => {
          return (
            <FileUploadInput
              value={row.original.documentation}
              onChange={(file) => {
                handleFileUpload(file, row.original.id || "new");
              }}
              readOnly={false}
              rowId={row.original.id || "new"}
            />
          );
        },
      },
      {
        id: currentImpactCategory.key,
        header: (
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <IconButton size="small" onClick={handlePreviousImpactCategory} style={{ padding: "4px" }}>
              <KeyboardArrowLeft fontSize="small" />
            </IconButton>
            <span>
              {currentImpactCategory.name} [{currentImpactCategory.unit}]
            </span>
            <IconButton size="small" onClick={handleNextImpactCategory} style={{ padding: "4px" }}>
              <KeyboardArrowRight fontSize="small" />
            </IconButton>
            <TableCompareTrigger />
          </div>
        ),
        accessor: currentImpactCategory.key,
        type: "custom",
        optional: true, // read-only output
        canSort: false,
        cell: ({ row }) => {
          const currentCategory = IMPACT_CATEGORIES[currentImpactCategoryIndex];
          const impactValue =
            row.original[currentCategory.key] ||
            row.original[`impact_${currentCategory.key}`] ||
            row.original.impact_categories?.[currentCategory.key] ||
            0;
          return <CustomInputComponent value={impactValue} readOnly />;
        },
        subtotalUnit: currentImpactCategory.unit,
        subtotalFixed: true,
        width: "200px",
      },
    ],
    [
      t,
      unitEnergyTypes,
      energyTypeDropdown,
      data,
      currentImpactCategory,
      currentImpactCategoryIndex,
      handlePreviousImpactCategory,
      handleNextImpactCategory,
    ]
  );

  const disableRules = useMemo(
    () => ({
      [impactCategoryAccessor]: () => true,
    }),
    [impactCategoryAccessor]
  );

  const subtotalColumns = useMemo(
    () => ({
      [impactCategoryAccessor]: { label: `Total ${currentImpactCategory.name}` },
    }),
    [impactCategoryAccessor, currentImpactCategory.name]
  );

  const memoizedDropdownData = useMemo(
    () => ({
      energy_type: energyTypeDropdown || [],
    }),
    [energyTypeDropdown]
  );

  // File upload component for new rows
  const FileUploadForNewRow = useCallback(
    ({ value, onChange }) => {
      return (
        <FileUploadInput
          value={value}
          onChange={(file) => {
            handleFileUpload(file, "new");
            if (onChange) onChange(file);
          }}
          readOnly={false}
          rowId="new"
        />
      );
    },
    [handleFileUpload]
  );

  return (
    <CustomTable
      columns={columns}
      data={data}
      setData={setData}
      onAdd={handleAdd}
      onEdit={handleEdit}
      onDelete={handleDelete}
      dropdownData={memoizedDropdownData}
      subtotalColumns={subtotalColumns}
      customInputs={useMemo(
        () => ({
          [impactCategoryAccessor]: CustomInputComponent,
          documentation: FileUploadForNewRow,
        }),
        [impactCategoryAccessor, FileUploadForNewRow]
      )}
      comparisonConfig={{ categories: IMPACT_CATEGORIES }}
      hiddenColumns={hiddenColumns}
      toggleColumnVisibility={toggleColumnVisibility}
      disableRules={disableRules}
      dynamicPlaceholders={dynamicPlaceholders}
    />
  );
};

export default ProductionMachines34_2aTable;
